import { v4 as uuid } from 'uuid'
import Bluebird from 'bluebird'
import CreateReducer from '../utils/createReducer'
import CreateActions from '../utils/createActions'
import networkWrapper from '../utils/networkWrapper'

import projectSelectors from '../selectors/project'

import snackbarActions from './snackbar'
import routerActions from './router'
import userSelectors from '../selectors/user'

const prefix = 'project'
const wrapper = networkWrapper.factory(prefix)

const initialState = {
  data: [],
  userMeta: null,
  project: null,
  joinWindow: false,
}

const reducers = {
  setProjects: (state, action) => {
    state.data = action.payload
  },
  setUserMeta: (state, action) => {
    state.userMeta = action.payload
  },
  setProject: (state, action) => {
    state.project = action.payload
  },
  setJoinWindow: (state, action) => {
    state.joinWindow = action.payload
  }
}

const userMetaDoc = (id) => firebase.firestore().collection('usermeta').doc(id)
const projectDoc = (id) => firebase.firestore().collection('project').doc(id)

const getUserMeta = async (id) => {
  if(!id) throw new Error(`id required`)
  const userMeta = await userMetaDoc(id).get()
  if(!userMeta.exists) {
    return {
      projects: [],
    }
  }
  else {
    const data = await userMeta.data()
    return Object.assign({}, data, {
      projects: data.projects || [],
    })
  }
}

const getProject = async (id) => {
  if(!id) throw new Error(`id required`)
  const project = await projectDoc(id).get()
  if(!project.exists) return null
  return project.data()
}

const deleteProject = async (id) => {
  if(!id) throw new Error(`id required`)
  await projectDoc(id).delete()
}

const saveUserMeta = async (id, data) => {
  if(!id) throw new Error(`id required`)
  if(!data) throw new Error(`data required`)
  await userMetaDoc(id).set(data)
}

const saveProjectData = async (id, data) => {
  if(!id) throw new Error(`id required`)
  if(!data) throw new Error(`data required`)
  await projectDoc(id).set(data)
}

const sideEffects = {

  loadProjects: ({
    
  } = {}) => wrapper('loadProjects', async (dispatch, getState) => {
    const userData = userSelectors.data(getState())
    if(!userData) throw new Error(`no user`)
    const userMeta = await getUserMeta(userData.id)
    if(userMeta.projects.length <= 0) {
      dispatch(actions.setUserMeta(userMeta))
      dispatch(actions.setProjects([]))
      return
    }
    const loadedProjects = await Bluebird.map(userMeta.projects, getProject)
    const projects = loadedProjects.filter(p => p)
    dispatch(actions.setProjects(projects))
    dispatch(actions.setUserMeta(userMeta))
  }),

  loadProject: (id) => wrapper('loadProject', async (dispatch, getState) => {
    const userData = userSelectors.data(getState())
    if(!userData) throw new Error(`no user`)
    const data = await getProject(id)
    const userMeta = await getUserMeta(userData.id)
    dispatch(actions.setProject(data))
    dispatch(actions.setUserMeta(userMeta))
    dispatch(actions.setJoinWindow(userMeta.projects.indexOf(id) < 0))
  }),

  addProject: ({

  } = {}) => wrapper('addProject', async (dispatch, getState) => {
    const userData = userSelectors.data(getState())
    if(!userData) throw new Error(`no user`)

    const newProject = {
      id: uuid(),
      owner: userData.id,
      name: 'New Project',
      items: [],
    }

    const userMeta = await getUserMeta(userData.id)
    const newMeta = Object.assign({}, userMeta, {
      projects: userMeta.projects.concat([newProject.id])
    })

    await saveProjectData(newProject.id, newProject)
    await saveUserMeta(userData.id, newMeta)
    await dispatch(actions.loadProjects())
    dispatch(snackbarActions.setSuccess(`new project created`))
    dispatch(routerActions.navigateTo('project', {
      id: newProject.id,
    }))
  }),

  updateProject: ({
    id,
    data,
  } = {}) => wrapper('updateProject', async (dispatch, getState) => {

    const currentData = projectSelectors.project(getState())
    const newData = Object.assign({}, currentData, data)
    
    dispatch(actions.setProject(newData))
    await saveProjectData(id, newData)
    
    dispatch(snackbarActions.setSuccess(`project upated`))
  }),

  deleteProject: ({
    id,
    name,
  } = {}) => wrapper('deleteProject', async (dispatch, getState) => {
    await deleteProject(id)
    await dispatch(actions.loadProjects())
    dispatch(snackbarActions.setSuccess(`${name} deleted`))
  }),

  importProject: ({
    data,
  } = {}) => wrapper('importProject', async (dispatch, getState) => {
    const userData = userSelectors.data(getState())
    if(!userData) throw new Error(`no user`)
    const newProject = Object.assign({}, data, {
      id: uuid(),
      owner: userData.id,
    })
    const userMeta = await getUserMeta(userData.id)
    const newMeta = Object.assign({}, userMeta, {
      projects: userMeta.projects.concat([newProject.id])
    })
    await saveProjectData(newProject.id, newProject)
    await saveUserMeta(userData.id, newMeta)
    await dispatch(actions.loadProjects())
    dispatch(snackbarActions.setSuccess(`project imported`))
    dispatch(routerActions.navigateTo('project', {
      id: newProject.id,
    }))
  }),

  joinProject: (id) => wrapper('joinProject', async (dispatch, getState) => {
    const userData = userSelectors.data(getState())
    if(!userData) throw new Error(`no user`)
    const userMeta = await getUserMeta(userData.id)
    const newMeta = Object.assign({}, userMeta, {
      projects: userMeta.projects.concat([id])
    })
    await saveUserMeta(userData.id, newMeta)
    await dispatch(actions.loadProjects())
    dispatch(actions.setJoinWindow(false))
    dispatch(snackbarActions.setSuccess(`project joined`))
  }),

}

const reducer = CreateReducer({
  initialState,
  reducers,
  prefix,
})

const actions = CreateActions({
  reducers,
  prefix,
  sideEffects,
})

export { actions, reducer }
export default actions